<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumb"></PageHeader>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <div class="form-group">
                  <input type="text" class="form-control" v-model="search" placeholder="Buscar por nome..." @input="debouncedSearch">
                </div>
              </div>
              <div class="col-sm-6 text-right">
                <button v-if="canCreate" type="button" class="btn btn-success btn-rounded" @click="openForm()">
                  <i class="mdi mdi-plus mr-1"></i> Novo bloqueio
                </button>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-centered table-hover">
                <thead>
                  <tr>
                    <th width="25%">Nome</th>
                    <th width="15%">Grupo</th>
                    <th width="10%" class="text-center">Voos</th>
                    <th width="15%" class="text-center">Aplicações</th>
                    <th width="10%" class="text-center">Status</th>
                    <th width="25%" class="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!flightBlocks.length">
                    <td colspan="6" class="text-center text-muted py-4">
                      Nenhum bloqueio cadastrado.
                    </td>
                  </tr>
                  <tr v-for="block in flightBlocks" :key="block.id">
                    <td>
                      <strong>{{ block.name }}</strong>
                      <div v-if="block.description" class="text-muted small">{{ block.description }}</div>
                    </td>
                    <td>
                      <span v-if="!block.group_id" class="badge bg-info text-light">Global</span>
                      <span v-else>{{ groupName(block.group_id) }}</span>
                    </td>
                    <td class="text-center">
                      {{ block.items_count || 0 }}
                    </td>
                    <td class="text-center">
                      {{ block.applications_count || 0 }}
                    </td>
                    <td class="text-center">
                      <span class="badge font-size-12 p-2" :class="block.is_active ? 'badge-success' : 'badge-secondary'">
                        {{ block.is_active ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                    <td class="text-right">
                      <button v-if="canEdit" type="button" class="btn btn-sm btn-outline-primary mr-1" @click="openForm(block.id)" title="Editar">
                        <i class="bx bx-edit"></i>
                      </button>
                      <button v-if="canCreate" type="button" class="btn btn-sm btn-outline-secondary mr-1" @click="duplicateBlock(block)" title="Duplicar">
                        <i class="bx bx-copy"></i>
                      </button>
                      <button v-if="canEdit" type="button" class="btn btn-sm mr-1" :class="block.is_active ? 'btn-outline-warning' : 'btn-outline-success'" @click="toggleBlock(block)" :title="block.is_active ? 'Desativar' : 'Ativar'">
                        <i :class="block.is_active ? 'bx bx-power-off' : 'bx bx-check'"></i>
                      </button>
                      <button v-if="canDelete" type="button" class="btn btn-sm btn-outline-danger" @click="deleteBlock(block)" title="Excluir">
                        <i class="bx bx-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage" @input="updateList"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal-form v-model="modalForm" ref="formBlock" @saved="updateList"></modal-form>
  </Layout>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import ModalForm from './Form.vue'

export default {
  components: { Layout, PageHeader, ModalForm },
  data () {
    return {
      title: 'Bloqueios Aéreos',
      breadcrumb: [
        { text: 'Configurações gerais', href: '#' },
        { text: 'Bloqueios Aéreos', active: true }
      ],
      search: '',
      searchDebounceTimer: null,
      modalForm: false,
      currentPage: 1,
      perPage: 20,
      totalRows: 0
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('flightBlocks', ['flightBlocks', 'pagination']),
    ...mapGetters('auth', ['hasPermission', 'isSuperAdmin']),
    rows () {
      return this.pagination.total || 0
    },
    canCreate () {
      return this.isSuperAdmin || this.hasPermission('flight-blocks.create')
    },
    canEdit () {
      return this.isSuperAdmin || this.hasPermission('flight-blocks.edit')
    },
    canDelete () {
      return this.isSuperAdmin || this.hasPermission('flight-blocks.delete')
    }
  },
  mounted () {
    this.updateList()
  },
  methods: {
    ...mapActions('flightBlocks', ['getAll', 'toggle', 'duplicate', 'remove']),
    updateList () {
      const params = {
        search: this.search || null,
        limit: this.perPage,
        page: this.currentPage
      }
      const loader = this.$loading.show()
      this.getAll(params).then(() => {
        loader.hide()
      }).catch(err => {
        loader.hide()
        console.error(err)
      })
    },
    debouncedSearch () {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.updateList()
      }, 400)
    },
    openForm (id = null) {
      this.$refs.formBlock.open(id)
      this.modalForm = true
    },
    async toggleBlock (block) {
      try {
        await this.toggle(block.id)
        successMessage(block.is_active ? 'Bloqueio desativado.' : 'Bloqueio ativado.', 2500)
        this.updateList()
      } catch (err) {
        errorMessage('Erro ao alterar status.')
      }
    },
    async duplicateBlock (block) {
      const result = await Swal.fire({
        title: 'Duplicar bloqueio?',
        text: 'Uma cópia será criada como "Inativa" para revisão.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Duplicar',
        cancelButtonText: 'Cancelar'
      })
      if (!result.isConfirmed) return
      try {
        await this.duplicate(block.id)
        successMessage('Bloqueio duplicado.', 2500)
        this.updateList()
      } catch (err) {
        errorMessage('Erro ao duplicar.')
      }
    },
    async deleteBlock (block) {
      if (block.applications_count > 0) {
        Swal.fire({
          title: 'Exclusão bloqueada',
          text: 'Este bloqueio já foi aplicado em participantes. Desative-o em vez de excluir.',
          icon: 'warning'
        })
        return
      }
      const result = await Swal.fire({
        title: 'Excluir bloqueio?',
        text: `"${block.name}" será permanentemente removido.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545'
      })
      if (!result.isConfirmed) return
      try {
        await this.remove(block.id)
        successMessage('Bloqueio excluído.', 2500)
        this.updateList()
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || 'Erro ao excluir.'
        errorMessage(msg)
      }
    },
    groupName (groupId) {
      // Fallback simples — se houver store de grupos, seria via computed com mapeamento por id
      return '#' + groupId
    }
  }
}
</script>
